import React from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { usePageMeta } from '../../lib/usePageMeta';

const endpoints = [
  {
    method: 'POST',
    path: '/api/search',
    auth: 'Bearer token',
    desc: 'Search licensed professionals by state, city, profession, or ZIP code. Deducts credits.',
    body: '{ "state": "FL", "profession": "Real Estate", "city": "Miami", "limit": 25, "offset": 0 }',
    response: '{ "results": [...], "count": 142, "credits_remaining": 4997 }',
  },
  {
    method: 'POST',
    path: '/api/live-search',
    auth: 'None',
    desc: 'Public homepage search. Returns up to 5 results by ZIP code. Rate limited.',
    body: '{ "zip": "33101" }',
    response: '{ "results": [...], "count": 12 }',
  },
  {
    method: 'POST',
    path: '/api/x402/search',
    auth: 'x402 payment header',
    desc: 'Pay-per-request search via x402 crypto protocol (USDC on Base). No account needed.',
    body: '{ "query": "real estate agents in Florida", "limit": 10 }',
    response: '{ "professionals": [...], "total": 89, "x402": { "amount": "0.03", "currency": "USDC" } }',
  },
  {
    method: 'POST',
    path: '/api/chat',
    auth: 'Bearer token',
    desc: 'AI-powered natural language query. Converts questions to SQL, returns results.',
    body: '{ "message": "How many active real estate agents are in Miami?", "sessionId": "..." }',
    response: '{ "message": { "content": "Found 2,847 active agents...", "results": [...] } }',
  },
  {
    method: 'GET',
    path: '/api/sources',
    auth: 'None',
    desc: 'List available data sources by state and profession with record counts.',
    body: null,
    response: '{ "sources": [{ "state": "FL", "professions": ["Real Estate", "Plumber"] }] }',
  },
  {
    method: 'GET',
    path: '/api/data-packs',
    auth: 'None',
    desc: 'List available data pack products with pricing and state availability.',
    body: null,
    response: '{ "packs": [{ "id": "single_state", "price": 297, "states": ["FL","NY"] }] }',
  },
  {
    method: 'POST',
    path: '/api/auth/register',
    auth: 'None',
    desc: 'Create a free account. 25 lookups/month, 5 exports/month included.',
    body: '{ "email": "you@company.com", "password": "..." }',
    response: '{ "token": "eyJ...", "user": { "id": "...", "subscription_tier": "free" } }',
  },
  {
    method: 'POST',
    path: '/api/auth/login',
    auth: 'None',
    desc: 'Authenticate with email/password. Returns JWT token.',
    body: '{ "email": "you@company.com", "password": "..." }',
    response: '{ "token": "eyJ...", "user": { "id": "...", "subscription_tier": "growth" } }',
  },
  {
    method: 'GET',
    path: '/api/health',
    auth: 'None',
    desc: 'System health check. Returns status and version.',
    body: null,
    response: '{ "status": "ok", "version": "1.0.0" }',
  },
];

const methodColor: Record<string, string> = {
  GET: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  POST: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
  PUT: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  DELETE: 'bg-red-500/10 text-red-400 border-red-500/30',
};

const ApiDocs: React.FC = () => {
  usePageMeta({ title: 'API Reference', description: 'Complete API documentation for ProGeoData. Search, verify, and export licensed professional data.', path: '/docs' });
  return (
    <div className="bg-slate-900 min-h-screen text-slate-300 font-sans antialiased">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-24 sm:px-6 lg:px-8">

        <div className="mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">API Reference</h1>
          <p className="text-lg text-slate-400 max-w-3xl">
            ProGeoData provides a RESTful API for searching and verifying licensed US professionals.
            All endpoints return JSON. Authentication uses Bearer tokens (JWT) obtained via <code className="text-sky-400">/api/auth/login</code>.
          </p>
        </div>

        <div className="mb-16 p-6 bg-slate-800/50 border border-slate-700 rounded-xl">
          <h2 className="text-lg font-bold text-white mb-3">Base URL</h2>
          <code className="text-sky-400 text-sm font-mono">https://progeodata.com/api</code>
          <h2 className="text-lg font-bold text-white mt-6 mb-3">Authentication</h2>
          <p className="text-sm text-slate-400 mb-2">Include your JWT token in the Authorization header:</p>
          <pre className="text-xs font-mono text-slate-300 bg-slate-950 p-3 rounded-lg">Authorization: Bearer eyJhbGciOiJIUzI1NiJ9...</pre>
          <h2 className="text-lg font-bold text-white mt-6 mb-3">Rate Limits</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-slate-900 p-3 rounded-lg"><span className="text-slate-500">Free</span><br /><span className="text-white font-bold">25/hr</span></div>
            <div className="bg-slate-900 p-3 rounded-lg"><span className="text-slate-500">Starter</span><br /><span className="text-white font-bold">100/hr</span></div>
            <div className="bg-slate-900 p-3 rounded-lg"><span className="text-slate-500">Growth</span><br /><span className="text-white font-bold">300/hr</span></div>
            <div className="bg-slate-900 p-3 rounded-lg"><span className="text-slate-500">Agency</span><br /><span className="text-white font-bold">1000/hr</span></div>
          </div>
        </div>

        <div className="space-y-6">
          {endpoints.map((ep, i) => (
            <details key={i} className="group border border-slate-700 rounded-xl overflow-hidden bg-slate-800/30">
              <summary className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-800/50 transition-colors">
                <span className={`px-2 py-0.5 rounded text-xs font-bold border ${methodColor[ep.method]}`}>
                  {ep.method}
                </span>
                <code className="text-sm font-mono text-white">{ep.path}</code>
                <span className="text-xs text-slate-500 ml-auto">{ep.auth}</span>
              </summary>
              <div className="border-t border-slate-700 p-4 space-y-3">
                <p className="text-sm text-slate-300">{ep.desc}</p>
                {ep.body && (
                  <div>
                    <span className="text-xs text-slate-500 uppercase font-bold">Request Body</span>
                    <pre className="text-xs font-mono text-emerald-400 bg-slate-950 p-3 rounded-lg mt-1">{ep.body}</pre>
                  </div>
                )}
                <div>
                  <span className="text-xs text-slate-500 uppercase font-bold">Response</span>
                  <pre className="text-xs font-mono text-slate-400 bg-slate-950 p-3 rounded-lg mt-1">{ep.response}</pre>
                </div>
              </div>
            </details>
          ))}
        </div>

        <div className="mt-16 p-6 bg-slate-800/50 border border-slate-700 rounded-xl">
          <h2 className="text-lg font-bold text-white mb-3">Quick Start</h2>
          <div className="space-y-3 text-sm text-slate-300">
            <p>1. <strong className="text-white">Create an account:</strong></p>
            <pre className="text-xs font-mono text-slate-400 bg-slate-950 p-3 rounded-lg">{`curl -X POST https://progeodata.com/api/auth/register \\
  -H 'Content-Type: application/json' \\
  -d '{"email":"you@company.com","password":"your_password"}'`}</pre>
            <p>2. <strong className="text-white">Search for professionals:</strong></p>
            <pre className="text-xs font-mono text-slate-400 bg-slate-950 p-3 rounded-lg">{`curl -X POST https://progeodata.com/api/search \\
  -H 'Authorization: Bearer YOUR_TOKEN' \\
  -H 'Content-Type: application/json' \\
  -d '{"state":"FL","profession":"Real Estate","limit":10}'`}</pre>
            <p>3. <strong className="text-white">Need more? Check out{' '}</strong>
              <a href="/api" className="text-sky-400 hover:underline">x402 API</a> for pay-per-request or{' '}
              <a href="/#pricing" className="text-sky-400 hover:underline">subscription plans</a>.
            </p>
          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default ApiDocs;
